import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Save, X, Lock } from "lucide-react";
import { toast } from "sonner";

import AdminPageHeader from "@/components/layout/AdminPageHeader";
import EstadoEventoBadge from "@/components/common/EstadoEventoBadge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { EVENTO_WIZARD, WIZARD_PLANTILLA } from "@/constants/testIds";
import {
  ESTADO_EVENTO,
  TIPO_ACCESO,
  getEventoById,
  guardarEvento,
  hidratarEventos,
} from "@/data/adminEventosStore";

import PasoInformacionGeneral from "@/screens/admin/eventos/PasoInformacionGeneral";
import PasoConfiguracion from "@/screens/admin/eventos/PasoConfiguracion";
import PasoPlanoMesas from "@/screens/admin/eventos/PasoPlanoMesas";
import SelectorPlantillaWizard from "@/screens/admin/eventos/SelectorPlantillaWizard";
import PasoSalonesAcceso from "@/screens/admin/eventos/PasoSalonesAcceso";
import SelectorModalidad from "@/screens/admin/eventos/SelectorModalidad";
import PasoVenta from "@/screens/admin/eventos/PasoVenta";
import PasoPublicacion from "@/screens/admin/eventos/PasoPublicacion";

const PASOS_MESAS = [
  { n: 1, titulo: "Información general", descripcion: "Datos básicos del evento." },
  { n: 2, titulo: "Configuración", descripcion: "Reglas y parámetros del evento." },
  { n: 3, titulo: "Plano y Mesas", descripcion: "Distribución de mesas del salón." },
  { n: 4, titulo: "Venta", descripcion: "Resumen comercial y capacidad." },
  { n: 5, titulo: "Publicación", descripcion: "Visibilidad y ciclo de vida." },
];

const PASOS_AG = [
  { n: 1, titulo: "Información general", descripcion: "Datos básicos del evento." },
  { n: 2, titulo: "Configuración", descripcion: "Reglas y parámetros del evento." },
  { n: 3, titulo: "Salones y boletos", descripcion: "Salones vendibles y precios." },
  { n: 4, titulo: "Venta", descripcion: "Resumen comercial y capacidad." },
  { n: 5, titulo: "Publicación", descripcion: "Visibilidad y ciclo de vida." },
];

const VALORES_INICIALES = {
  // Modalidad (Sprint 57 · F4A) · default MESAS para eventos nuevos.
  modalidad: "MESAS",
  // Paso 1 · Información general
  nombre: "",
  subtitulo: "",
  descripcionCorta: "",
  imagenPrincipal: "",
  imagenPrincipalKey: "",
  galeria: [],
  fecha: "",
  hora: "",
  salon: "",
  salones: [],
  tipoEvento: "",
  edadMinima: 18,
  tipoAcceso: TIPO_ACCESO.SOLO_SOCIOS,
  estado: ESTADO_EVENTO.BORRADOR,
  // Paso 2 · Configuración
  limiteBoletosPorUsuario: 10,
  ventaInicioFecha: "",
  ventaInicioHora: "",
  ventaCierreFecha: "",
  ventaCierreHora: "",
  permitirPausarVenta: true,
  permitirListaEspera: false,
  permitirReasignacionMesas: true,
  permitirCambioNombres: true,
  notifInicioVenta: false,
  notifPausaVenta: false,
  notifCambioSalon: false,
  notifCambioMesa: false,
  notifCancelacion: false,
  // Paso 3 · Plano y Mesas
  mesas: [],
  capacidad: 0,
  // Paso 3 (Acceso General) · salones cargados desde backend (no es columna)
  salonesAcceso: [],
  // Paso 4 · Configuración comercial (los precios se editan en Paso 1)
  precios: {},
};

/**
 * Espejo frontend de `mesas_vendibles_sin_precio` (backend precios.py). Deriva
 * si el evento MESAS tiene mesas vendibles (DISPONIBLE) sin precio autoritativo.
 * Al derivarlo del estado real (no de un flag de sesión), el aviso de
 * reconfiguración PERSISTE tras recargar y desaparece al capturar el precio.
 */
const faltanPreciosMesas = (valores) => {
  if ((valores.modalidad || "MESAS") !== "MESAS") return false;
  const mesas = Array.isArray(valores.mesas) ? valores.mesas : [];
  const precios =
    valores.precios && typeof valores.precios === "object" ? valores.precios : {};
  const salonEvento = (valores.salones && valores.salones[0]) || valores.salon || "";
  const valoresUnicos = new Set(
    Object.values(precios)
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n) && n > 0),
  );
  const precioDe = (mesa) => {
    if (Number(mesa?.precio) > 0) return Number(mesa.precio);
    if (mesa?.salon && Number(precios[mesa.salon]) > 0) return Number(precios[mesa.salon]);
    if (salonEvento && Number(precios[salonEvento]) > 0) return Number(precios[salonEvento]);
    if (valoresUnicos.size === 1) return [...valoresUnicos][0];
    return null;
  };
  return mesas.some(
    (m) => String(m?.estado || "DISPONIBLE").toUpperCase() === "DISPONIBLE" && precioDe(m) === null,
  );
};

export default function EventoWizard({ modo }) {
  const navigate = useNavigate();
  const params = useParams();
  const [step, setStep] = useState(1);
  const [valores, setValores] = useState(VALORES_INICIALES);
  const [errores, setErrores] = useState({});
  const editandoId = modo === "editar" ? params.eventoId : null;

  // Derivado del estado real → persiste tras recargar (F5) y desaparece al
  // capturar el precio del salón nuevo. Reemplaza el antiguo flag en memoria.
  const preciosPendientes = useMemo(() => faltanPreciosMesas(valores), [valores]);

  const esAG = (valores.modalidad || "MESAS") === "ACCESO_GENERAL";
  const PASOS = esAG ? PASOS_AG : PASOS_MESAS;
  // Frontend informativo: sólo un borrador puede cambiar de modalidad.
  // El backend conserva la última palabra (guardia F2).
  const modalidadBloqueada =
    (valores.estado || ESTADO_EVENTO.BORRADOR) !== ESTADO_EVENTO.BORRADOR;

  const cambiarModalidad = async (nueva) => {
    if (modalidadBloqueada || nueva === valores.modalidad) return;
    setValores((v) => ({ ...v, modalidad: nueva }));
    toast.info(
      "Se cambió la modalidad. La configuración anterior se conserva, pero no estará activa mientras uses esta modalidad.",
    );
    if (editandoId) {
      const res = await guardarEvento({ id: editandoId, modalidad: nueva });
      if (res && res.error) {
        // La guardia backend rechazó el cambio (p.ej. 409): revertir y avisar.
        setValores((v) => ({ ...v, modalidad: valores.modalidad }));
        toast.error(
          typeof res.error === "string" && res.error !== "network"
            ? res.error
            : "No se pudo cambiar la modalidad de este evento.",
        );
      }
    }
  };

  useEffect(() => {
    let cancelado = false;
    const cargar = async () => {
      await hidratarEventos();
      if (cancelado) return;
      if (editandoId) {
        const ev = getEventoById(editandoId);
        if (ev) {
          // Compat: si el evento viejo sólo tenía `salon` y no `salones`,
          // derivamos el array. Al guardar se persistirá el nuevo shape.
          const salones =
            Array.isArray(ev.salones) && ev.salones.length > 0
              ? ev.salones
              : ev.salon
                ? [ev.salon]
                : [];
          setValores({ ...VALORES_INICIALES, ...ev, salones });
        } else {
          toast.error("Evento no encontrado");
          navigate("/admin/eventos", { replace: true });
        }
      }
      // Modo "nuevo": el estado ya se inicializó con VALORES_INICIALES en
      // useState. NO re-seteamos aquí para no pisar la modalidad u otros
      // campos que el usuario pudo cambiar mientras resolvía la hidratación.
    };
    cargar();
    return () => {
      cancelado = true;
    };
  }, [editandoId, navigate]);

  const onChangeCampo = (campo, valor) => {
    setValores((v) => ({ ...v, [campo]: valor }));
    setErrores((e) => (e[campo] ? { ...e, [campo]: undefined } : e));
  };

  // Sprint 58 · F2 · Rehidratación tras aplicar una plantilla de mesas.
  const handlePlantillaAplicada = async (resp) => {
    await hidratarEventos();
    const ev = getEventoById(editandoId);
    if (ev) {
      const salones =
        Array.isArray(ev.salones) && ev.salones.length
          ? ev.salones
          : ev.salon
            ? [ev.salon]
            : [];
      setValores({ ...VALORES_INICIALES, ...ev, salones });
    }
    const pendiente = !!(resp && resp.salonCambio && resp.preciosRequierenConfiguracion);
    if (pendiente) {
      toast.warning(
        "El salón cambió: los precios anteriores se eliminaron. Configura los precios en el Paso 4 antes de publicar.",
      );
    } else {
      toast.info("Plantilla aplicada. Se conservan los precios existentes.");
    }
  };

  /**
   * Reglas Fase B:
   *  - Paso 1: `imagenPrincipal` NO es obligatoria para guardar borrador
   *    ni para avanzar entre pasos. Sí lo será para publicar (Paso 5).
   *  - Salón(es): ahora es multi-selección; se admite legacy `salon` string.
   *  - Precios por salón: requeridos > 0 en Paso 1 cuando el salón está
   *    seleccionado, para poder avanzar.
   */
  const validarPaso1 = () => {
    const errs = {};
    if (!valores.nombre || !valores.nombre.trim())
      errs.nombre = "Falta el nombre del evento.";
    if (!valores.fecha) errs.fecha = "Selecciona la fecha del evento.";
    if (!valores.hora) errs.hora = "Selecciona la hora del evento.";
    const salones = Array.isArray(valores.salones) ? valores.salones : [];
    if (salones.length === 0) {
      errs.salones = "Selecciona al menos un salón para el evento.";
    } else {
      const precios = valores.precios || {};
      const invalidos = salones.filter(
        (s) => !Number.isFinite(Number(precios[s])) || Number(precios[s]) <= 0,
      );
      if (invalidos.length > 0) {
        errs.precios = `Captura un precio mayor a cero para: ${invalidos.join(", ")}.`;
      }
    }
    setErrores(errs);
    return Object.keys(errs).length === 0;
  };

  const validarPaso2 = () => {
    const errs = {};
    if (!valores.tipoAcceso)
      errs.tipoAcceso = "Selecciona el tipo de acceso.";
    if (valores.edadMinima === "" || valores.edadMinima === null || valores.edadMinima === undefined)
      errs.edadMinima = "Indica la edad mínima.";
    if (
      valores.limiteBoletosPorUsuario === "" ||
      valores.limiteBoletosPorUsuario === null ||
      valores.limiteBoletosPorUsuario === undefined ||
      Number(valores.limiteBoletosPorUsuario) < 1
    )
      errs.limiteBoletosPorUsuario = "Indica el límite máximo de boletos por usuario.";
    if (!valores.ventaInicioFecha)
      errs.ventaInicioFecha = "Selecciona la fecha de inicio de venta.";
    if (!valores.ventaCierreFecha)
      errs.ventaCierreFecha = "Selecciona la fecha de cierre de venta.";
    setErrores(errs);
    return Object.keys(errs).length === 0;
  };

  const validarPaso3 = () => {
    const errs = {};
    const mesas = Array.isArray(valores.mesas) ? valores.mesas : [];
    if (mesas.length === 0) {
      errs.mesas = "Agrega al menos una mesa antes de continuar.";
    } else {
      const cap = mesas.reduce(
        (s, m) => s + (Number(m && m.capacidad) || 0),
        0,
      );
      if (cap <= 0) errs.mesas = "La capacidad total del evento debe ser mayor a cero.";
    }
    // Duplicados en el mismo salón
    const porSalon = {};
    mesas.forEach((m) => {
      const clave = `${m.salon || (valores.salones && valores.salones[0]) || valores.salon || ""}::${m.numero}`;
      porSalon[clave] = (porSalon[clave] || 0) + 1;
    });
    const dup = Object.entries(porSalon).find(([, c]) => c > 1);
    if (dup) {
      errs.mesas = "Hay números de mesa duplicados dentro del mismo salón.";
    }
    setErrores(errs);
    return Object.keys(errs).length === 0;
  };

  const validarPaso4 = () => {
    const errs = {};
    const mesas = Array.isArray(valores.mesas) ? valores.mesas : [];
    const disponibles = mesas.filter((m) => (m && m.estado) === "DISPONIBLE");
    const capDisponible = disponibles.reduce(
      (s, m) => s + (Number(m && m.capacidad) || 0),
      0,
    );
    if (disponibles.length === 0) {
      errs.capacidad = "No hay mesas disponibles para la venta. Regresa al Paso 3.";
    } else if (capDisponible <= 0) {
      errs.capacidad = "La capacidad vendible del evento es cero.";
    }
    setErrores(errs);
    return Object.keys(errs).length === 0;
  };

  /**
   * Mensajes específicos. Sustituye la advertencia genérica:
   *   "No se pudo guardar el evento. Intenta de nuevo."
   */
  const mensajeErrorPersistencia = (persistido) => {
    if (persistido && persistido.error) {
      if (typeof persistido.error === "string" && persistido.error !== "network") {
        return persistido.error;
      }
      if (persistido.error === "network") {
        return "No se pudo conectar con el servidor. Intenta más tarde.";
      }
    }
    return "No se pudo guardar el evento con los datos actuales.";
  };

  const guardar = async ({ avanzar }) => {
    if (step === 1 && !validarPaso1()) {
      const primerError = Object.values(errores)[0];
      toast.error(primerError || "Revisa el paso 1 antes de continuar.");
      return;
    }
    if (step === 2 && !validarPaso2()) {
      const primerError = Object.values(errores)[0];
      toast.error(primerError || "Revisa el paso 2 antes de continuar.");
      return;
    }
    if (step === 3 && !esAG && !validarPaso3()) {
      const primerError = Object.values(errores)[0];
      toast.error(primerError || "Revisa el plano antes de continuar.");
      return;
    }
    if (step === 4 && !esAG && !validarPaso4()) {
      const primerError = Object.values(errores)[0];
      toast.error(primerError || "Revisa la capacidad antes de continuar.");
      return;
    }
    // Compat backend: `salon` (singular) = primer salón del array.
    const salones = Array.isArray(valores.salones) ? valores.salones : [];
    const salonPrincipal = salones[0] || valores.salon || "";
    const preciosLimpios = Object.fromEntries(
      Object.entries(valores.precios || {}).filter(([s]) => salones.includes(s)),
    );
    const base = { ...valores, salones, salon: salonPrincipal, precios: preciosLimpios };
    const payload = editandoId
      ? { ...base, id: editandoId }
      : (() => {
          const copia = { ...base };
          delete copia.id;
          return copia;
        })();
    const persistido = await guardarEvento(payload);
    if (!persistido || persistido.error) {
      toast.error(mensajeErrorPersistencia(persistido));
      return;
    }
    toast.success(
      editandoId ? "Cambios guardados." : "Evento creado como borrador.",
    );
    if (avanzar) {
      setStep((s) => Math.min(5, s + 1));
    }
    if (!editandoId) {
      navigate(`/admin/eventos/${persistido.id}/editar`, { replace: true });
    }
  };

  const tituloEncabezado = editandoId ? "Editar evento" : "Nuevo evento";
  const estadoActual = valores.estado || ESTADO_EVENTO.BORRADOR;

  return (
    <div
      data-testid={EVENTO_WIZARD.screen}
      className="flex flex-col gap-8 pb-24 md:pb-28"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <AdminPageHeader
            eyebrow="Portal administrativo · Eventos"
            title={tituloEncabezado}
            description={
              editandoId
                ? "Actualiza los datos del evento a lo largo de los pasos."
                : "Captura la información general para crear un borrador. Podrás continuar con los siguientes pasos después."
            }
          />
        </div>
        <div className="flex items-center gap-3">
          <EstadoEventoBadge estado={estadoActual} />
          <Button
            asChild
            variant="ghost"
            data-testid={EVENTO_WIZARD.btnCancelar}
          >
            <Link to="/admin/eventos" aria-label="Salir del asistente">
              <X className="w-4 h-4 mr-2" />
              Salir
            </Link>
          </Button>
        </div>
      </div>

      {/* Stepper */}
      <StepIndicator step={step} pasos={PASOS} onSelect={(n) => setStep(n)} />

      {valores.estado === ESTADO_EVENTO.EVENTO_CERRADO && (
        <div
          data-testid="wizard-cerrado-banner"
          className="rounded-2xl border hairline bg-muted/50 px-5 py-4 flex items-center gap-3 text-sm"
        >
          <Lock className="w-4 h-4 text-muted-foreground" strokeWidth={1.7} />
          <span>
            Este evento está <b>cerrado</b> y no admite modificaciones. Se muestra
            únicamente en modo consulta.
          </span>
        </div>
      )}

      {/* Contenido del paso actual */}
      <div className="grid grid-cols-1 gap-6">
        {step === 1 && (
          <>
            <SelectorModalidad
              value={valores.modalidad}
              bloqueado={modalidadBloqueada}
              onSelect={cambiarModalidad}
            />
            <PasoInformacionGeneral
              valores={valores}
              onChange={onChangeCampo}
              errores={errores}
            />
          </>
        )}
        {step === 2 && (
          <PasoConfiguracion
            valores={valores}
            onChange={onChangeCampo}
            errores={errores}
          />
        )}
        {step === 3 &&
          (esAG ? (
            <PasoSalonesAcceso valores={valores} onChange={onChangeCampo} />
          ) : (
            <>
              {editandoId && (
                <SelectorPlantillaWizard
                  eventoId={editandoId}
                  salonActual={(valores.salones && valores.salones[0]) || valores.salon || ""}
                  onAplicado={handlePlantillaAplicada}
                />
              )}
              {preciosPendientes && (
                <div
                  data-testid={WIZARD_PLANTILLA.avisoPrecios}
                  className="rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200 px-4 py-3 text-sm flex items-start gap-2"
                >
                  <span className="mt-0.5">⚠️</span>
                  <span>
                    El salón cambió por la plantilla aplicada. Los precios anteriores se
                    eliminaron; configura los precios en el <b>Paso 4 · Venta</b> antes de
                    publicar (la publicación está bloqueada por precio no configurado).
                  </span>
                </div>
              )}
              <PasoPlanoMesas
                valores={valores}
                onChange={onChangeCampo}
                errores={errores}
              />
            </>
          ))}
        {step === 4 && (
          <PasoVenta
            valores={valores}
            onChange={onChangeCampo}
            errores={errores}
            onGoToStep={(n) => setStep(n)}
          />
        )}
        {step === 5 && (
          <PasoPublicacion
            valores={valores}
            onGoToStep={(n) => setStep(n)}
            onEventoActualizado={(ev) => setValores((v) => ({ ...v, ...ev }))}
          />
        )}
      </div>

      {/* Sticky nav (Fase B): siempre accesible sin scroll excesivo */}
      <div
        data-testid={EVENTO_WIZARD.stickyNav}
        className="fixed bottom-0 left-0 right-0 md:left-72 z-30 border-t hairline glass"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-3 flex items-center justify-between gap-3">
          <div className="hidden sm:flex flex-col leading-tight min-w-0">
            <span className="overline text-muted-foreground">
              Paso {step} / 5
            </span>
            <span className="text-sm font-medium truncate">
              {PASOS[step - 1]?.titulo}
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap ml-auto">
            <Button
              type="button"
              variant="outline"
              data-testid={EVENTO_WIZARD.btnAnterior}
              disabled={step === 1}
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              className="rounded-xl"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Anterior
            </Button>
            {(step === 1 || step === 2 || step === 3 || step === 4) && (
              <Button
                type="button"
                variant="outline"
                data-testid={EVENTO_WIZARD.btnGuardar}
                onClick={() => guardar({ avanzar: false })}
                className="rounded-xl"
                size="sm"
              >
                <Save className="w-4 h-4 mr-1.5" />
                Guardar borrador
              </Button>
            )}
            {step < 5 ? (
              <Button
                type="button"
                data-testid={EVENTO_WIZARD.btnSiguiente}
                onClick={() =>
                  step >= 1 && step <= 4
                    ? guardar({ avanzar: true })
                    : setStep((s) => s + 1)
                }
                className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
                size="sm"
              >
                Siguiente
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button
                asChild
                variant="outline"
                data-testid={EVENTO_WIZARD.btnFinalizar}
                className="rounded-xl"
                size="sm"
              >
                <Link to="/admin/eventos">
                  <Check className="w-4 h-4 mr-1.5" />
                  Volver al listado
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- StepIndicator ---------- */

function StepIndicator({ step, pasos, onSelect }) {
  const total = pasos.length;
  return (
    <ol className="sticky top-[72px] z-20 bg-muted/40 backdrop-blur-md rounded-xl p-1 grid grid-cols-2 sm:grid-cols-5 gap-1 sm:gap-2">
      {pasos.map((p) => {
        const activo = step === p.n;
        const completado = step > p.n;
        return (
          <li key={p.n}>
            <button
              type="button"
              data-testid={EVENTO_WIZARD.stepIndicator(p.n)}
              onClick={() => onSelect(p.n)}
              className={cn(
                "w-full text-left rounded-xl border hairline px-3 py-2 transition-all",
                "bg-card hover:border-border",
                activo && "ring-1 ring-primary/50 border-primary/40",
                completado && !activo && "opacity-90",
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "w-6 h-6 rounded-full grid place-items-center text-xs font-medium border shrink-0",
                    activo
                      ? "bg-primary text-primary-foreground border-primary"
                      : completado
                        ? "bg-accent/20 text-accent border-accent/40"
                        : "bg-muted text-muted-foreground border-border/70",
                  )}
                >
                  {completado ? (
                    <Check className="w-3 h-3" strokeWidth={2.2} />
                  ) : (
                    p.n
                  )}
                </span>
                <div className="min-w-0 hidden sm:block">
                  <p className="text-[10px] overline text-muted-foreground">
                    Paso {p.n} / {total}
                  </p>
                  <p
                    className={cn(
                      "text-xs leading-tight truncate",
                      activo ? "font-medium text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {p.titulo}
                  </p>
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
